import React from "react";
import { Button, DownloadIcon } from "@pd/pd-ui-kit";
import { get, isString } from "lodash";
import { useTranslation } from "react-i18next";

import Wrapper from "~/shared/ui/Wrapper";
import { DealerInfoPanel } from "~/entity/Dealer";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { useSetSelectOption } from "~/hooks/SelectValue";
import { getIndicatorValue } from "~/libs/getData";
import {
  useGetGrFullReportsLazyQuery,
  useGetRatingDataQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import { useNotify } from "~/shared/hooks/useNotify";
import { Select } from "~/shared/ui/Select";
import { TableV2 } from "~/shared/ui/TableV2";

import { getGRColumns } from "./model/tableAdapter";
import {
  GLOBAL_RATING_OPTIONS,
  GLOBAL_RATING_TYPE,
  NO_ACTUAL_OPTIONS,
  SELECT_GR,
} from "./options";
import { GRFilter } from "./ui/GRFilter";

import s from "./GR.less";

const GR = () => {
  const { t } = useTranslation();
  const { searchField, isFull } = useGetLocationData();
  const currentDealerId = useCurrentDealerId();
  const currentYear = new Date().getFullYear();
  const { hasPermission } = usePermissions();
  const { notifyReport } = useNotify();
  const [GetGrFullReportsLazyQuery, GrFullReportsLazyQuery] =
    useGetGrFullReportsLazyQuery({
      onCompleted(data) {
        const hash = get(data, "GlobalRatingGetter.getFullReports.fileHash");

        hash && notifyReport(hash);
      },
      fetchPolicy: "no-cache",
    });

  const actualIsFull = isFull === "show_all_data";

  const lang = localStorage.getItem("language");
  const { data, error, loading } = useGetRatingDataQuery({
    variables: {
      DealerId: currentDealerId,
      year: searchField ? +searchField : currentYear,
      isFull: actualIsFull,
    },
    fetchPolicy: "no-cache",
  });

  const selectOption = useSetSelectOption();
  const setData = useSetLocationData();
  const setIsFull = (value: string) => {
    setData("isFull", value);
  };

  const GetGrFullReports = () => {
    GetGrFullReportsLazyQuery({
      variables: {
        DealerId: currentDealerId,
        year: searchField ? +searchField : currentYear,
        lang,
        isFull: actualIsFull,
      },
    });
  };
  const preparedIsFull = SELECT_GR.map((option) =>
    selectOption(option, "selectGR"),
  );

  const RatingData = get(data, "GlobalRatingGetter.getRatingData", {});

  const fieldNameByQuarter = {
    quarter1: {
      quarter: {
        name: "IndicatorQuarter1",
        index: 2,
      },
    },
    quarter2: {
      prev: "quarter1",
      quarter: {
        name: "IndicatorQuarter2",
        index: 3,
      },
      between: {
        name: "IndicatorQuarter1_2",
        index: 4,
      },
    },
    quarter3: {
      prev: "quarter2",
      quarter: {
        name: "IndicatorQuarter3",
        index: 5,
      },
      between: {
        name: "IndicatorQuarter2_3",
        index: 6,
      },
    },
    quarter4: {
      prev: "quarter3",
      quarter: {
        name: "IndicatorQuarter4",
        index: 7,
      },
      between: {
        name: "IndicatorQuarter3_4",
        index: 8,
      },
    },
  };
  const dataRows = [];
  const PrevIdList: { [key: number]: number } = {};

  for (const quarter in RatingData) {
    for (const data of RatingData[quarter]) {
      if (data.PrevElementId) {
        if (PrevIdList[data.PrevElementId]) {
          PrevIdList[data.id] = PrevIdList[data.PrevElementId];
        } else {
          PrevIdList[data.id] = data.PrevElementId;
        }
      } else {
        PrevIdList[data.id] = data.id;
      }
    }
  }

  for (const key in PrevIdList) {
    if (PrevIdList[PrevIdList[key]]) {
      PrevIdList[key] = PrevIdList[PrevIdList[key]];
    }
  }

  const backgroundRows = {};
  let sortingById = [];
  const backgroundCell = {};

  if (RatingData && "__typename" in RatingData) {
    delete (RatingData as any).__typename;
  }
  for (const quarter in RatingData) {
    for (let index = 0; index < RatingData[quarter].length; index++) {
      const data = RatingData[quarter][index];

      const id = PrevIdList[data.PrevElementId] || PrevIdList[data.id];
      if (
        id === data.id &&
        !(
          fieldNameByQuarter[quarter].prev &&
          RatingData[fieldNameByQuarter[quarter].prev] &&
          RatingData[fieldNameByQuarter[quarter].prev][index] &&
          RatingData[fieldNameByQuarter[quarter].prev][index].id === data.id
        )
      ) {
        backgroundCell[id] = (
          fieldNameByQuarter[quarter].between ||
          fieldNameByQuarter[quarter].quarter
        ).index;
      }

      if (lang !== "ru" && !!data.nameEn) {
        data.name = data.nameEn;
      }

      sortingById.push({ id: id, sort: data.sort || 0 });
      if (data.type === GLOBAL_RATING_TYPE.title && isFull) {
        backgroundRows[id] = "e69632";
      }
      if (data.type === GLOBAL_RATING_TYPE.total) {
        backgroundRows[id] = "ec8c22";
      }

      if (dataRows[id]) {
        dataRows[id].number =
          (isString(dataRows[id].number)
            ? [dataRows[id].number]
            : dataRows[id].number) || [];
        if (!dataRows[id].number.includes(data.number)) {
          dataRows[id].number = [...dataRows[id].number, data.number];
        }

        dataRows[id].name =
          (isString(dataRows[id].name)
            ? [dataRows[id].name]
            : dataRows[id].name) || [];
        if (!dataRows[id].name.includes(data.name)) {
          dataRows[id].name = [...dataRows[id].name, data.name];
        }
      } else {
        dataRows[id] = { number: data.number, name: data.name };
      }

      dataRows[id][fieldNameByQuarter[quarter].quarter.name] =
        getIndicatorValue(
          data[fieldNameByQuarter[quarter].quarter.name],
          data.round,
          data.format,
        );

      if (fieldNameByQuarter[quarter].between) {
        dataRows[id][fieldNameByQuarter[quarter].between.name] =
          getIndicatorValue(
            data[fieldNameByQuarter[quarter].between.name],
            data.round,
            data.format,
          );
      }

      if (data.IndicatorYearValue) {
        dataRows[id].IndicatorYearValue = getIndicatorValue(
          data.IndicatorYearValue,
          data.round,
          data.format,
        );
      }

      if (data.IndicatorYearPercent) {
        dataRows[id].IndicatorYearPercent = getIndicatorValue(
          data.IndicatorYearPercent,
          data.round,
          data.format,
        );
      }

      if (data.type) {
        dataRows[id].type = data.type;
      }
      if (data.ParentId) {
        dataRows[id].ParentId = data.ParentId;
      }
    }
  }

  sortingById = sortingById.sort((prev, next) => {
    if (prev.sort < next.sort) {
      return -1;
    }
    if (prev.sort > next.sort) {
      return 1;
    }
    return 0;
  });

  const isPrintById = {};
  const newDataRows = [];

  if (!error) {
    for (const sort of sortingById) {
      if (!dataRows[sort.id] || isPrintById[sort.id]) {
        continue;
      }

      dataRows[sort.id].length = 11;
      isPrintById[sort.id] = true;
      newDataRows.push({
        ...dataRows[sort.id],
        name: dataRows[sort.id].name[dataRows[sort.id].name.length - 1],
        number: dataRows[sort.id].number[dataRows[sort.id].number.length - 1],
        typeName: dataRows[sort.id].type,
        ParentId: dataRows[sort.id].ParentId,
        nameNoActual:
          dataRows[sort.id].name.slice(0, -1).length > 0 &&
          `${dataRows[sort.id].name.slice(0, -1).join(" / ")} /`,
        numberNoActual:
          dataRows[sort.id].number.slice(0, -1).length > 0 &&
          `${dataRows[sort.id].number.slice(0, -1).join(" / ")} /`,
      });
    }
  }

  const columns = getGRColumns(
    t,
    GLOBAL_RATING_OPTIONS,
    NO_ACTUAL_OPTIONS,
    actualIsFull,
  );

  return (
    <Wrapper.Content
      title={t("globalRating.globalsRatings")}
      filterComponent={
        <div className={s.filterArea}>
          <DealerInfoPanel />
          <div className={s.filtersRow}>
            <GRFilter />
            <Select
              size="small"
              className={s.selectView}
              onChange={setIsFull}
              options={preparedIsFull}
              value={isFull || SELECT_GR[1]}
            />
          </div>
        </div>
      }
    >
      <div className={s.actionsRow}>
        {hasPermission("globalRatings.fullReports.get") && (
          <Button
            color={"secondary"}
            variant={"filled"}
            bold
            fontSize="12"
            size="medium"
            radius={30}
            icon={<DownloadIcon size={16} fill="#495a64" />}
            onClick={GetGrFullReports}
            loading={GrFullReportsLazyQuery.loading}
          >
            {t("q.save_excel")}
          </Button>
        )}
      </div>
      <TableV2
        columns={columns}
        dataSource={loading ? [] : newDataRows}
        loading={loading}
        wrapperClassName={s.gr_table}
        pagination={false}
      />
    </Wrapper.Content>
  );
};

export default GR;
